<template>
    <component :is="card ? 'card' : 'div'"
        :class="['entity-alerts mt-1 mb-3', { 'no-alerts': !alerts.length, sticky: false, collapsed: !showingAlerts || (!alerts.length && !loading) }]">
        <div :class="['d-flex align-items-center mb-2', 'justify-content-' + alignLegend]">
            <div v-if="$slots.default" class="flex-fill">
                <slot />
            </div>
            <div>
                <slot name="accessory" />
            </div>
            <div :class="['alert-legend', { 'cursor-pointer': alerts.length }]" @click.prevent="toggleAll">
                <small>Alerts</small>
                <div class="icons flex-fill">
                    <b-spinner v-if="loading" :show="true" small />
                    <small v-else-if="!alerts.length" style="color: black; opacity: 1;">No Alerts</small>
                    <template v-else>
                        <span v-for="w in allAlertsForLegend" :key="w.name">
                            <i :id="`entity-alert-${w.name}`" v-if="w.icon"
                                :class="`${w.icon} text-${w.variant ?? 'info'}`" />
                            <b-tooltip :target="`entity-alert-${w.name}`" placement="bottom">
                                <strong>
                                    {{ w.title + (w.count ? ` (${w.count})` : "") + (w.message ? ":" : "") }}
                                </strong>
                                {{ w.message }}
                            </b-tooltip>
                        </span>
                    </template>
                </div>
                <div v-if="alerts.length || loading" class="toggle">
                    <i :class="{ 'fa fa-fw': true, 'fa-chevron-down': true, 'fa-rotate-180': !showingAlerts }"
                        aria-hidden="true" style="transition: all .2s;"></i>
                </div>
            </div>
        </div>

        <b-collapse :visible="!loading && showingAlerts && (standardAlerts.length > 0 || smallAlerts.length > 0)">
            <!-- Standard Alerts -->
            <div class="alerts standard-alerts" :data-alert-count="standardAlerts.length" v-if="standardAlerts.length">
                <b-alert v-for="w in standardAlerts" :key="w.name" :variant="w.variant ?? 'info'" :show="true" fade
                    class="m-0 d-flex flex-column" :data-test-name="w.name">
                    <div class="d-flex align-items-center w-100">
                        <div class="primary-icon">
                            <i v-if="w.icon" :class="w.icon" />
                        </div>
                        <div class="flex-fill d-flex flex-column">
                            <div>
                                <strong>
                                    {{ w.title }}
                                </strong>
                                <span v-if="w.count" class="count">
                                    {{ w.count }}
                                </span>
                            </div>
                            <div v-if="w.message">
                                {{ w.message }}
                            </div>
                        </div>
                        <b-button v-if="w.children.length" size="sm" @click="toggleDetails(w)"
                            class="d-flex align-items-center">
                            Details
                            <i :class="{ 'fa fa-fw cursor-pointer': true, 'fa-chevron-right': true, 'fa-rotate-90': showDetails.includes(w.name) }"
                                aria-hidden="true" style="transition: all .2s;"></i>
                        </b-button>
                        <b-overlay :show="loadingAction && selectedAlert === w" spinner-small spinner-variant="primary"
                            rounded>
                            <b-button v-if="w.action" :to="!w.action.startsWith('#') ? w.action : undefined" size="sm"
                                class="m-0" @click="handleAction(w)">
                                {{ w.actionLabel }}
                            </b-button>
                        </b-overlay>
                    </div>
                    <div v-if="showDetails.includes(w.name) && w.children.length" class="pl-5 mt-2 d-flex flex-column"
                        style="gap: 4px;">
                        <div class="sub" v-for="sw in alerts.filter(a => a.parent === w.name)">
                            <div class="flex-fill">
                                <strong>{{ sw.title }}</strong>
                                {{ sw.message }}
                            </div>

                            <b-overlay :show="loadingAction && selectedAlert === sw" spinner-small
                                spinner-variant="primary" rounded>
                                <b-button v-if="sw.action" :to="!sw.action.startsWith('#') ? sw.action : undefined"
                                    size="sm" class="m-0" @click="handleAction(sw)">
                                    {{ sw.actionLabel }}
                                </b-button>
                            </b-overlay>
                        </div>
                    </div>
                </b-alert>
            </div>

            <!-- Small Alerts -->
            <div class="alerts small-alerts" :data-alert-count="smallAlerts.length" v-if="smallAlerts.length">
                <b-alert v-for="w in smallAlerts" :key="w.name" :variant="w.variant ?? 'info'" :show="true" fade
                    class="m-0 d-flex flex-column small-alert" :data-test-name="w.name">
                    <div class="d-flex align-items-center w-100">
                        <div class="primary-icon small">
                            <i v-if="w.icon" :class="w.icon" />
                        </div>
                        <div class="flex-fill d-flex flex-column">
                            <div class="small-content">
                                <strong>{{ w.title }}</strong>
                                <span v-if="w.count" class="count small">{{ w.count }}</span>
                                <span v-if="w.message" class="message">{{ w.message }}</span>
                            </div>
                        </div>
                        <b-overlay :show="loadingAction && selectedAlert === w" spinner-small spinner-variant="primary"
                            rounded>
                            <b-button v-if="w.action" :to="!w.action.startsWith('#') ? w.action : undefined" size="sm"
                                class="m-0" @click="handleAction(w)">
                                {{ w.actionLabel }}
                            </b-button>
                        </b-overlay>
                    </div>
                </b-alert>
            </div>
        </b-collapse>

        <!--Rx Transfers-->
        <RxTransferListModalComponent v-if="alerts.some(a => a.action === '#transfer')"
            ref="rxTransfersModalComponent" />

        <!-- Patient Cases -->
        <b-modal v-if="alerts.some(a => a.action === '#patient-cases')" title="Patient Cases" hide-footer
            id="patient-cases-modal" size="lg">
            <CasesListComponent :default-items="casesList" hide-patient small show-status :default-page-size="25"
                @update="getCasesForPatient" />
        </b-modal>

        <!-- Doctor Follow-Ups -->
        <b-modal v-if="alerts.some(a => a.action === '#followup')" :visible="!!selectedFollowUpCase"
            title="Doctor Follow-Up Case" size="xl" hide-footer static @hidden="selectedFollowUpCase = null">
            <DoctorFollowUpCaseForm v-if="selectedFollowUpCase" v-model="selectedFollowUpCase" @submitted="fetchData" />
        </b-modal>

        <!-- Tasks -->
        <b-modal id="tasks-modal" v-if="alerts.some(a => a.action === '#tasks')" title="Tasks" hide-footer size="xl">
            <TasksPage :patient="patientObj" size="sm" :card="false" />
        </b-modal>

        <!-- Consultations -->
        <b-modal id="consultations-modal" title="Consultations" size="xl" hide-footer>
            <ConsultationListComponent ref="consultationList" :enable-cancel="true" :enable-edit="true"
                :patient-id="+patientId" :hide-patient="true" modal-id="complete-consultation-alerts" />
        </b-modal>
    </component>
</template>

<script lang="ts">
import RxTransferListModalComponent from '@/components/Patient/RxTransferListModalComponent.vue';
import { Patient } from '@/models/Patient';
import { PrescriberFollowUpCase } from '@/models/PrescriberFollowUpCase';
import { Prescription } from '@/models/Prescription';
import { Prescriber } from '@/models/Prescriber';
import { Drug } from '@/models/Drug/Drug';
import { DrugSource } from '@/models/Drug/DrugProduct';
import axios from 'axios';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import ConsultationListComponent from './Consultation/ConsultationListComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Configuration } from '@/models/Configuration';
import { AlertPresentationStyle, EntityAlertConfigurations } from '@/models/AlertConfiguration';

export interface Alert {
    parent?: string;
    name: string;
    icon: string;
    variant: string;
    title: string;
    message?: string;
    action?: string;
    actionLabel?: string;
    count?: number;
    value?: string | number;
}

interface AlertWithChildren extends Alert {
    children: Alert[];
}

@Component({
    name: "EntityAlertsComponent",
    components: {
        CasesListComponent: () => import("@/components/Cases/CasesListComponent.vue"),
        DoctorFollowUpCaseForm: () => import('@/components/Doctor/DoctorFollowUpCaseForm.vue'),
        TasksPage: () => import("@/pages/Tasks/TasksPage.vue"),
        RxTransferListModalComponent,
        ConsultationListComponent
    },
})
export default class EntityAlertsComponent extends Vue {

    @Prop({ default: null }) protected prescription!: Prescription | null;
    @Prop({ default: null }) protected patient!: Patient | null;
    @Prop({ default: null }) protected prescriber!: Prescriber | null;
    @Prop({ default: null }) protected drug!: Drug | null;
    @Prop({ default: null }) protected drugSource!: DrugSource | null;
    @Prop({ default: null }) protected storeId!: number | null;
    @Prop({ default: null }) protected fillDate!: Date | null;
    @Prop({ default: null }) protected rxStatus!: number | null;
    @Prop({ default: null }) protected expirationDate!: Date | null;
    @Prop({ default: "left" }) protected alignLegend!: string;
    @Prop({ default: false, type: Boolean }) protected card!: boolean;
    @Prop({ default: null }) protected additionalAlerts!: Alert[] | null;

    @Ref('rxTransfersModalComponent') rxTransfersModalComponent!: RxTransferListModalComponent;
    @Ref('consultationList') private consultationList!: ConsultationListComponent;

    protected alerts: Alert[] = [];
    protected configuration: EntityAlertConfigurations | null = null;

    protected showingAlerts = true;

    protected loading = false;
    protected selectedAlert: Alert | null = null;
    protected loadingAction = false;

    protected showDetails: string[] = [];

    private fetchDataDebounceTimer: number | null = null;

    mounted() {
        this.fetchConfiguration();
        if (this.patient || this.prescription || this.prescriber || this.drug)
            this.fetchData();
    }

    @Watch("patient")
    shouldUpdatePatientAlerts(newPatient?: Patient, oldPatient?: Patient) {
        if (newPatient?.id != oldPatient?.id) {
            this.showingAlerts = true;
            this.debouncedFetchData();
        }
    }

    @Watch("prescription")
    shouldUpdatePrescriptionAlerts(newScript?: Prescription, oldScript?: Prescription) {
        if (newScript?.rxID != oldScript?.rxID) {
            this.showingAlerts = true;
            this.debouncedFetchData();
        }
    }

    @Watch("prescriber")
    shouldUpdatePrescriberAlerts(newPrescriber?: Prescriber, oldPrescriber?: Prescriber) {
        if (newPrescriber?.id != oldPrescriber?.id)
            this.debouncedFetchData();
    }

    @Watch("drug")
    shouldUpdateDrugAlerts(newDrug?: Drug, oldDrug?: Drug) {
        if (newDrug?.drugId != oldDrug?.drugId)
            this.debouncedFetchData();
    }

    @Watch("drugSource")
    shouldUpdateDrugSourceAlerts(newDrugSource?: DrugSource, oldDrugSource?: DrugSource) {
        if (newDrugSource !== oldDrugSource)
            this.debouncedFetchData();
    }

    @Watch("storeId")
    shouldUpdateStoreIdAlerts(newStoreId?: number, oldStoreId?: number) {
        if (newStoreId !== oldStoreId)
            this.debouncedFetchData();
    }

    @Watch("fillDate")
    shouldUpdateFillDateAlerts(newFillDate?: Date, oldFillDate?: Date) {
        if (newFillDate !== oldFillDate)
            this.debouncedFetchData();
    }

    @Watch("rxStatus")
    shouldUpdateRxStatusAlerts(newRxStatus?: number, oldRxStatus?: number) {
        if (newRxStatus !== oldRxStatus)
            this.debouncedFetchData();
    }

    @Watch("expirationDate")
    shouldUpdateExpirationDateAlerts(newExpirationDate?: Date, oldExpirationDate?: Date) {
        if (newExpirationDate !== oldExpirationDate)
            this.debouncedFetchData();
    }

    public refresh() {
        this.fetchData();
    }

    private debouncedFetchData() {
        // Clear any existing timer
        if (this.fetchDataDebounceTimer !== null) {
            window.clearTimeout(this.fetchDataDebounceTimer);
        }

        // Set new timer to fetch data after 300ms of inactivity
        this.fetchDataDebounceTimer = window.setTimeout(() => {
            this.fetchData();
            this.fetchDataDebounceTimer = null;
        }, 300);
    }

    async fetchConfiguration() {
        try {
            const response = await axios.get<EntityAlertConfigurations>("/api/alert/configuration");
            this.configuration = response.data;
        } catch (error) {
            console.warn("Failed to load configuration for alerts:", error);
        }
    }

    async fetchData() {
        try {
            this.loading = true;

            const resp = await axios.get<Alert[]>("/api/alert", {
                params: {
                    patientId: this.patient?.id,
                    rxId: this.prescription?.rxID,
                    storeId: this.storeId ?? this.prescription?.storeID,
                    prescriberId: this.prescriber?.id,
                    drugId: this.drug?.drugId,
                    drugSource: this.drugSource ?? this.drug?.source,
                    fillDate: this.fillDate ?? this.prescription?.fillDate,
                    rxStatus: this.rxStatus ?? this.prescription?.status,
                    expirationDate: this.expirationDate ?? this.prescription?.expirationDate
                }
            });
            this.alerts = resp.data;
        }
        catch (err) {
            this.alerts = [
                {
                    title: "Error Loading Alerts",
                    message: "There was an error loading alerts",
                    icon: "fa fa-bug",
                    name: "errorLoading",
                    variant: "danger"
                }
            ];
        } finally {
            this.loading = false;
        }
    }

    toggleAll() {
        this.showingAlerts = !this.showingAlerts;
    }

    toggleDetails(alert: Alert) {
        if (this.showDetails.includes(alert.name)) {
            this.showDetails = this.showDetails.filter(d => d !== alert.name);
        } else {
            this.showDetails.push(alert.name);
        }
    }

    get displayAlerts(): AlertWithChildren[] {
        const allAlerts = [
            ...this.alerts,
            ...(this.additionalAlerts ?? [])
        ];

        return allAlerts
            .filter(a => !a.parent)
            .filter(a => this.isAlertEnabled(a.name))
            .map(a => {
                const children = this.alerts.filter(sa => sa.parent === a.name);
                const alertConfig = this.configuration?.[a.name];

                return {
                    ...a,
                    variant: alertConfig?.color || a.variant,
                    count: a.count ?? children.length,
                    children,
                    presentationStyle: alertConfig?.presentationStyle || AlertPresentationStyle.Standard
                };
            })
            .sort((a, b) => {
                // Sort by presentation style: standard first, then small, then icon
                const styleOrder = { standard: 0, small: 1, icon: 2 };
                const aStyle = (a as any).presentationStyle || AlertPresentationStyle.Standard;
                const bStyle = (b as any).presentationStyle || AlertPresentationStyle.Standard;
                return styleOrder[aStyle] - styleOrder[bStyle];
            });
    }

    get standardAlerts(): AlertWithChildren[] {
        return this.displayAlerts.filter(alert =>
            (alert as any).presentationStyle === AlertPresentationStyle.Standard
        );
    }

    get smallAlerts(): AlertWithChildren[] {
        return this.displayAlerts.filter(alert =>
            (alert as any).presentationStyle === AlertPresentationStyle.Small
        );
    }

    get iconOnlyAlerts(): AlertWithChildren[] {
        return this.displayAlerts.filter(alert =>
            (alert as any).presentationStyle === AlertPresentationStyle.Icon
        );
    }

    get allAlertsForLegend(): AlertWithChildren[] {
        // Show all alerts in the legend, including icon-only ones
        return this.displayAlerts;
    }

    isAlertEnabled(alertName: string): boolean {
        if (!this.configuration) return true;
        const config = this.configuration[alertName];
        return config?.enabled !== false;
    }

    async handleAction(alert: Alert) {
        try {
            this.selectedAlert = alert;
            this.loadingAction = true;

            switch (alert.action?.toLowerCase()) {
                case "#transfer":
                    await this.rxTransfersModalComponent.getUnfinishedRxTransfers(this.patientObj);
                    this.rxTransfersModalComponent.showModal();
                    break;
                case "#patient-cases":
                    await this.getCasesForPatient();
                    this.$bvModal.show("patient-cases-modal");
                    break;
                case "#followup":
                    await this.getFollowUpCase(Number(alert.value));
                    break;
                case "#tasks":
                    this.$bvModal.show("tasks-modal");
                    break;
                case "#consultations":
                    this.$bvModal.show("consultations-modal");
                    this.$nextTick(() => {
                        (this.consultationList as any).getConsultation();
                    });
                    break;
                case "#pendingorders":
                    this.$router.push({ name: "OnlineOrderQueue" });
                    break;
            }
        }
        catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        finally {
            this.loadingAction = false;
        }
    }

    get patientId() {
        if (this.patient?.id) return this.patient.id;
        return this.prescription?.patientID;
    }

    get patientObj() {
        if (this.patient) return this.patient;
        return { id: Number(this.patientId) } as Patient;
    }

    protected casesList = [];

    async getCasesForPatient() {
        try {
            if (!this.patientId) return;
            const response = await axios.get(`/Patient/${this.patientId}/Cases`);
            this.casesList = response.data;
        } catch (error) {
            console.error("Problem loading insurance Patient Cases", error);
        }
    }

    protected selectedFollowUpCase: PrescriberFollowUpCase | null = null;

    async getFollowUpCase(id: number) {
        try {
            if (!this.patientId) return;
            //TODO: Optimize
            const resp = await axios.get<Array<PrescriberFollowUpCase>>(`/prescriber/follow-up-by-patient/${this.patientId}`, {
                params: { includeUpcoming: true }
            });

            this.selectedFollowUpCase = resp.data.find(i => i.id === id) ?? null;
        } catch (error) {

        }
    }
}


</script>

<style scoped>
.sticky {
    position: sticky;
    top: 0;
    z-index: 50;
}

.card.collapsed {
    background: transparent;
    box-shadow: none;
    border: none;
    transition: all .3s;
}

.card ::v-deep(.card-body) {
    transition: all .3s;
}

.card.collapsed ::v-deep(.card-body) {
    padding: 0;
}

.card.collapsed .alert-legend {
    background: white;
}

.card.no-alerts .alert-legend:hover {
    box-shadow: none;
}

.entity-alerts {
    --red: #ff5050;
    --purple: rebeccapurple;
    --green: rgb(0, 179, 0);
    --orange: orange;
    --turquoise: rgb(0, 167, 153);
    --pink: rgb(255, 143, 162);
    --blue: rgb(0, 140, 233);
    --black: rgb(17, 17, 17);
}

.count {
    font-weight: 900;
    background: white;
    border-radius: 10px;
    color: black;
    padding: 0 6px;
    font-variant-numeric: tabular-nums;
}

.sub {
    background: rgba(255, 255, 255, .4);
    padding: 6px;
    border-radius: 4px;
    display: flex;
    align-items: center;
}

.alerts {
    display: grid;
    gap: 7px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 520px), 1fr));

    strong {
        font-size: 1.1em;
    }
}

.alerts[data-alert-count='1'] {
    grid-template-columns: 1fr;
}

@media (min-width: 1100px) {
    .alerts[data-alert-count='2'] {
        grid-template-columns: 1fr 1fr;
    }
}

.alert-legend {
    background: rgba(0, 0, 0, .08);
    padding: 10px;
    font-size: 1.7em;
    position: sticky;
    top: 0;
    text-align: center;
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 5px;
    border-radius: 10px;
    transition: all .3s;
    min-height: 43px;

    .icons {
        display: flex;
        gap: 13px;
    }

    .cursor-pointer {
        cursor: pointer;
    }

    small {
        font-size: .6em;
        font-weight: 600 !important;
        text-transform: uppercase;
        letter-spacing: 1px;
        opacity: .7;
        display: inline-block;
        margin-right: 5px;
    }

    .toggle {
        background: rgb(0, 78, 129);
        color: white;
        display: inline-block;
        margin: -20px -10px -20px 10px;
        padding: 10px 5px;
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
    }
}

.alert-legend:hover {
    box-shadow: 2px 2px 10px rgba(0, 0, 0, .15);
}

.alerts .primary-icon i {
    background: white;
    color: black;
    width: 2em;
    height: 2em;
    border-radius: 50%;
    margin: -10px;
    margin-right: 10px;
    box-shadow: 0 0 6px rgba(0, 0, 0, .25);
    text-align: center;
    font-size: 1.2em !important;
    line-height: 2;
}

.alerts .btn {
    background: white;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: .5px;
    padding: 5px 8px;
}

/* Red Alert */
.alerts .alert-danger i,
.alerts .alert-danger .btn,
.alerts .alert-red i,
.alerts .alert-red .btn,
.text-danger,
.text-red,
.alerts .alert-danger .count,
.alerts .alert-red .count {
    color: var(--red);
}

.alerts .alert-danger,
.alerts .alert-red {
    background: var(--red);
}

/* Purple Alert */
.alerts .alert-purple i,
.alerts .alert-purple .btn,
.text-purple,
.alerts .alert-purple .count {
    color: var(--purple);
}

.alerts .alert-purple {
    background: var(--purple);
}

/* Green Alert */
.alerts .alert-success i,
.alerts .alert-success .btn,
.alerts .alert-green i,
.alerts .alert-green .btn,
.text-success,
.text-green {
    color: var(--green);
}

.alerts .alert-success,
.alerts .alert-green {
    background: var(--green);
}

/* Orange Alert */
.alerts .alert-warning i,
.alerts .alert-warning .btn,
.alerts .alert-orange i,
.alerts .alert-orange .btn,
.text-warning,
.text-orange,
.alerts .alert-orange .count {
    color: var(--orange);
}

.alerts .alert-orange {
    background: var(--orange);
}

/* Turquoise Alert */
.alerts .alert-turquoise i,
.alerts .alert-turquoise .btn,
.text-turquoise,
.alerts .alert-turquoise .count {
    color: var(--turquoise);
}

.alerts .alert-turquoise {
    background: var(--turquoise);
}

/* Pink Alert */
.alerts .alert-pink i,
.alerts .alert-pink .btn,
.text-pink,
.alerts .alert-pink .count {
    color: var(--pink);
}

.alerts .alert-pink {
    background: var(--pink);
}

/* Blue Alert */
.alerts .alert-primary i,
.alerts .alert-primary .btn,
.alerts .alert-blue i,
.alerts .alert-blue .btn,
.text-primary,
.text-blue,
.alerts .alert-blue .count {
    color: var(--blue);
}

.alerts .alert-primary,
.alerts .alert-blue {
    background: var(--blue);
}

/* Black Alert */
.alerts .alert-black i,
.alerts .alert-black .btn,
.text-black {
    color: var(--black);
}

.alerts .alert-black {
    background: var(--black);
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
    transition: opacity .2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Small Alert Styles */
.small-alerts {
    margin-top: 10px;
}

.small-alert {
    padding-top: .6em;
    padding-bottom: .6em;
    font-size: 0.9em;
}

.small-alert .primary-icon.small i {
    width: 1.5em !important;
    height: 1.5em !important;
    font-size: 1em !important;
    line-height: 1.5 !important;
    margin: -5px !important;
    margin-right: 8px !important;
}

.small-alert .small-content {
    display: flex;
    align-items: center;
    gap: 8px;
}

.small-alert .small-content strong {
    font-size: 1em;
}

.small-alert .count.small {
    font-size: 0.8em;
    padding: 0 4px;
    font-weight: 700;
}

.small-alert .message {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9em;
}

/* .card.entity-alerts {
    .alert-legend {
        width: 100%;
    }
} */
</style>
